import React, {useEffect, useRef, useState} from "react";
import {DateRangePicker, FocusedInputShape} from 'react-dates';
import {Container} from "../containers/Container";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  AffiliateDashboardTab,
  fetchAffiliateData,
  fetchAffiliateInfo,
  fetchAffiliatePromoCodes,
  reset,
  selectAffiliateShareCode,
  selectAffiliateAppShareUrl,
  selectAffiliateShopShareUrl,
  selectAffiliateStatus,
  selectCashPayouts,
  selectCommissions,
  selectEndDate,
  selectError,
  selectIsLoadingData,
  selectIsLoadingInfo,
  selectPromoCodes,
  selectSelectedTab,
  selectStartDate,
  selectStoreCreditPayouts,
  selectTotalPromoCodeCount, setDateRange,
  setSelectedTab
} from "../../redux/affiliate/affiliateSlice";
import Moment from "moment";
import {Datum, Serie} from "@nivo/line";
import {StoreEmptyStateView} from "../storeFeed/StoreEmptyStateView";
import {ApiError} from "../../errors/apiError";
import {LoadingSpinnerIcon, WarningColorIcon} from "../icons";
import {useRouter} from "next/router";
import {useModalRouting} from "../../utils/useModalRouting";
import {Heading2, Heading4, HeroHeading2, Paragraph} from "../text";
import Skeleton from "react-loading-skeleton";
import {BodyInput} from "../inputs";
import {Button} from "../buttons/Button";
import {SegmentedControl, SegmentedControlItem} from "../segmentedControls/SegmentedControl";
import {AffiliateDashboardTable} from "./AffiliateDashboardTable";
import {AffiliateDashboardChart} from "./AffiliateDashboardChart";
import {CurrencyCode} from "../../models/currencyCode";
import {RealmServiceError} from "../../errors/realmServiceError";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export const AffiliateDashboardView: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { createRoute } = useModalRouting()

  const [pageNumber, setPageNumber] = useState(1)
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null)
  const commissions = useAppSelector(selectCommissions)
  const cashPayouts = useAppSelector(selectCashPayouts)
  const storeCreditPayouts = useAppSelector(selectStoreCreditPayouts)
  const promoCodes = useAppSelector(selectPromoCodes)
  const promoCodesCount = useAppSelector(selectTotalPromoCodeCount)
  const selectedTab = useAppSelector(selectSelectedTab)
  const appShareUrl = useAppSelector(selectAffiliateAppShareUrl)
  const shopShareUrl = useAppSelector(selectAffiliateShopShareUrl)
  const shareCode = useAppSelector(selectAffiliateShareCode)
  const status = useAppSelector(selectAffiliateStatus)
  const startDate = useAppSelector(selectStartDate)
  const endDate = useAppSelector(selectEndDate)
  const error = useAppSelector(selectError)
  const isLoadingData = useAppSelector(selectIsLoadingData)
  const isLoadingInfo = useAppSelector(selectIsLoadingInfo)

  const segmentedControlItems: SegmentedControlItem<AffiliateDashboardTab>[] = [
    { value: 'commissions', id: 'commissions' },
    { value: 'cash_payouts', id: 'cash_payouts' },
    { value: 'store_credit_payouts', id: 'store_credit_payouts' },
    { value: 'perks', id: 'perks' },
  ]

  useEffect(() => {
    if (router.query.onboard === 'true') {
      router.push(
        createRoute({
          rootPath: '/affiliate',
          name: 'login',
          query: { config: 'sign_in' },
          returnTo: { name: 'affiliate_onboard' }
        }),
        undefined,
        { shallow: true }
      );
    }
  }, [router.query.onboard])

  useEffect(() => {
    dispatch(fetchAffiliatePromoCodes(pageNumber))
  }, [dispatch, pageNumber])

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Affiliate Dashboard' }))

    dispatch(reset())
    dispatch(fetchAffiliateData())
    dispatch(fetchAffiliateInfo())
  }, [])

  const commissionData: { [key: number]: number } = {}
  const cashPayoutData: { [key: number]: number } = {}
  const storeCreditPayoutData: { [key: string]: { [key: number]: number } } = {}

  const range: number = Moment(endDate).add(1, 'day').diff(startDate, 'days')
  const days = [...Array(range)].map((i, index) => index);

  days.forEach(day => {
    const date = Moment(startDate).startOf('day').add(day, 'day').unix()

    commissionData[date] = 0
    cashPayoutData[date] = 0
  })

  commissions.forEach(commission => {
    const createdAt = Moment(commission.createdAt).startOf('day').unix()
    const amount = commissionData[createdAt] ?? 0
    commissionData[createdAt] = amount + (commission.amount ?? 0)
  })

  cashPayouts.forEach(payout => {
    const createdAt = Moment(payout.createdAt).startOf('day').unix()
    const amount = cashPayoutData[createdAt] ?? 0
    cashPayoutData[createdAt] = amount + (payout.amount ?? 0)
  })

  storeCreditPayouts.forEach(payout => {
    const vendorId = payout.vendor?.id + '|' + payout.vendor?.name
    if (vendorId) {
      let vendorPayouts = storeCreditPayoutData[vendorId]

      if (!vendorPayouts) {
        vendorPayouts = {}

        days.forEach(day => {
          const date = Moment(startDate).add(day, 'day').startOf('day').unix()

          vendorPayouts[date] = 0
        })
      }

      const createdAt = Moment(payout.createdAt).startOf('day').unix()

      const amount = vendorPayouts[createdAt] ?? 0
      vendorPayouts[createdAt] = amount + (payout.amount ?? 0)
      storeCreditPayoutData[vendorId] = vendorPayouts
    }
  })

  const [copyAppShareText, setCopyAppShareText] = useState(t('affiliate:affiliate_title_copy_code'))
  const [copyShopShareText, setCopyShopShareText] = useState(t('affiliate:affiliate_title_copy_code'))

  let currencyCode: CurrencyCode | undefined
  let chartTitle: string
  let chartData: Serie[] = []
  switch (selectedTab) {
    case "commissions":
      currencyCode = commissions[0]?.currencyCode
      chartTitle = t('affiliate:affiliate_title_commissions')
      const commissionDataPoints: Datum[] = Object.entries(commissionData).map(([key, value]) => {
        return {
          x: parseInt(key),
          y: value
        }
      }).sort((a, b) => a.x - b.x)

      chartData = [
        {
          id: 'commissions',
          data: commissionDataPoints
        }
      ]
      break;
    case "cash_payouts":
      currencyCode = cashPayouts[0]?.currencyCode
      chartTitle = t('affiliate:affiliate_title_payouts')
      const cashPayoutDataPoints: Datum[] = Object.entries(cashPayoutData).map(([key, value]) => {
        return {
          x: parseInt(key),
          y: value
        }
      }).sort((a, b) => a.x - b.x)

      chartData = [
        {
          id: 'commissions',
          data: cashPayoutDataPoints
        }
      ]
      break;
    case "store_credit_payouts":
      currencyCode = storeCreditPayouts[0]?.currencyCode
      chartTitle = t('affiliate:affiliate_title_store_credit')
      chartData = Object.entries(storeCreditPayoutData).map(([vendorId, vendorPayouts]) => {
        const dataPoints: Datum[] = Object.entries(vendorPayouts).map(([key, value]) => {
          return {
            x: parseInt(key),
            y: value
          }
        }).sort((a, b) => a.x - b.x)

        return {
          id: vendorId,
          data: dataPoints,
        }
      })
      break;
    case "perks":
      chartTitle = t('affiliate:affiliate_title_perks')
      break;
  }

  const emptyStateButtonTitle = error instanceof ApiError && error.code === 'UNAUTHORIZED' || error instanceof RealmServiceError && error.code === 'NOT_LOGGED_IN'
    ? t('auth:auth_title_sign_in')
    : t('store:store_empty_state_button_try_again')

  return <Container>
    <div className={`${error ? '' : 'hidden'} flex-1 flex flex-col justify-center items-center`}>
      <StoreEmptyStateView
        onClick={() => {
          if (error instanceof ApiError && error.code === 'UNAUTHORIZED' || error instanceof RealmServiceError && error.code === 'NOT_LOGGED_IN') {
            router.push(
              createRoute({ name: 'login', query: { config: 'sign_in' }, reloadOnClose: true }),
              undefined,
              { shallow: true }
            );
          } else {
            dispatch(reset())
            if (pageNumber === 1) {
              dispatch(fetchAffiliatePromoCodes(pageNumber))
            } else {
              setPageNumber(1)
            }
            dispatch(fetchAffiliateData())
            dispatch(fetchAffiliateInfo())
          }
        }}
        buttonTitle={emptyStateButtonTitle}
        error={error}
        icon={<WarningColorIcon />}
        isLoading={isLoadingData}
      />
    </div>

    <HeroHeading2 className={`mb-8 ${error ? 'hidden' : ''}`}>{isLoadingInfo ? <Skeleton className="!w-2/5" /> : t('affiliate:affiliate_title_affiliate_dashboard')}</HeroHeading2>

    <div className={`mb-8 ${error ? 'hidden' : ''}`}>
      <div className={`${appShareUrl ? '' : 'hidden'} mb-8`}>
        <Heading2 className="mb-2">{t('affiliate:affiliate_title_premium_share_code')}</Heading2>
        <Paragraph className="mb-2">{t('affiliate:affiliate_subtitle_premium_share_code')}</Paragraph>
        <div className="flex space-x-3 items-center">
          <div className="flex-1 flex-grow">
            <BodyInput disabled={true} value={appShareUrl} />
          </div>
          <Button onClick={() => {
            appShareUrl && navigator.clipboard.writeText(appShareUrl)

            setCopyAppShareText('Copied!')
            setTimeout(() => {
              setCopyAppShareText(t('affiliate:affiliate_title_copy_code'))
            }, 2000)
          }}>
            {copyAppShareText}
          </Button>
        </div>
      </div>

      <div className={`${shopShareUrl ? '' : 'hidden'} mb-12`}>
        <Heading2 className="mb-2">{t('affiliate:affiliate_title_meal_delivery_share_code')}</Heading2>
        <Paragraph className="mb-2">{t('affiliate:affiliate_subtitle_meal_delivery_share_code')}</Paragraph>
        <div className="flex space-x-3 items-center">
          <div className="flex-1 flex-grow">
            <BodyInput disabled={true} value={shopShareUrl} />
          </div>
          <Button onClick={() => {
            shopShareUrl && navigator.clipboard.writeText(shopShareUrl)

            setCopyShopShareText('Copied!')
            setTimeout(() => {
              setCopyShopShareText(t('affiliate:affiliate_title_copy_code'))
            }, 2000)
          }}>
            {copyShopShareText}
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row space-y-3 space-x-0 md:space-y-0 md:space-x-3 items-center mb-8">
        <SegmentedControl
          className="flex-shrink-0"
          selectedItemId={selectedTab}
          items={segmentedControlItems}
          renderItem={(item) => {
            let title: string
            switch (item.value) {
              case "commissions":
                title = t('affiliate:affiliate_title_commissions')
                break;
              case "cash_payouts":
                title = t('affiliate:affiliate_title_payouts')
                break;
              case "store_credit_payouts":
                title = t('affiliate:affiliate_title_store_credit')
                break;
              case "perks":
                title = t('affiliate:affiliate_title_perks')
                break;
            }
            return <Heading4 className="text-center overflow-x-hidden whitespace-nowrap overflow-ellipsis">{title}</Heading4>
          }}
          onItemClick={(item) => dispatch(setSelectedTab(item.value))}
        />

        <div className="flex-grow hidden md:block" />

        <div className={`flex-shrink-0 mb-8 ${selectedTab === 'perks' ? 'hidden' : ''}`}>
          <DateRangePicker
            startDate={Moment(startDate)}
            startDateId="start_date"
            endDate={Moment(endDate)}
            numberOfMonths={1}
            endDateId="end_date"
            isOutsideRange={() => false}
            onDatesChange={({ startDate, endDate }) => startDate && endDate && dispatch(setDateRange({ startDate: startDate?.toDate(), endDate: endDate?.toDate() }))} // PropTypes.func.isRequired,
            focusedInput={focusedInput}
            onFocusChange={focusedInput => setFocusedInput(focusedInput)}
          />
        </div>
      </div>

      <div className={`${isLoadingData ? '' : 'hidden'} h-screen-1/2 w-full flex-1 flex items-center justify-center`}>
        <LoadingSpinnerIcon className="h-8 w-8 lg:h-10 lg:w-10 animate-spin text-fg-primary" />
      </div>

      <div className={`mb-8 ${selectedTab === 'perks' ? 'hidden' : ''} ${isLoadingData ? 'hidden' : ''}`}>
        <AffiliateDashboardChart currencyCode={currencyCode} title={chartTitle} isLoading={isLoadingData} data={chartData} />
      </div>

      <div className={`${isLoadingData ? 'hidden' : ''}`}>
        <AffiliateDashboardTable
          commissions={commissions}
          cashPayouts={cashPayouts}
          storeCreditPayouts={storeCreditPayouts}
          promoCodes={promoCodes}
          promoCodePageNumber={pageNumber}
          onLoadMorePromoCodesClicked={() => {
            setPageNumber(pageNumber + 1)
          }}
        />
      </div>
    </div>
  </Container>
}